<template>
  <v-form @submit.prevent="phoneVerification">
    <div class="mb-6" :class="{ 'mt-6': isDynamicFormStep }">
      {{ $t('phoneVerification.subtitle', { phone: censoredPhone }) }}
    </div>
    <label>{{ $t('phoneVerification.label') }}</label>
    <CodeInput
      v-model="$v.verificationCode.$model"
      :error="$v.verificationCode.$error || phoneVerificationError"
      :valid="!!verificationCode.length && !$v.verificationCode.$invalid"
      @keyup="phoneVerificationError = false"
      @update="delayTouch($v.verificationCode)"
      @finish="phoneVerification"
    />
    <v-row v-if="$v.$anyError || phoneVerificationError" class="error--text mt-0 pl-3">
      <div v-if="$v.$anyError">{{ $t('phoneVerification.inputError') }}</div>
      <div v-if="phoneVerificationError">{{ $t('phoneVerification.codeError') }}</div>
    </v-row>
    <div class="text-right">
      <span
        :class="{
          link: !countdown,
          'grey-darken-1--text': !!countdown
        }"
        @click="
          () => {
            !countdown ? $emit('resendSms') : null;
          }
        "
      >
        {{ `${$t('phoneVerification.sendAgain')}${countdown > 0 ? ` (${countdown}s)` : ''}` }}
      </span>
    </div>
  </v-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { required } from 'vuelidate/lib/validators';
import dynamicForms from '@/mixins/dynamicForms';
import userAppData from '@/mixins/userAppData';
import redirectParams from '@/mixins/redirectParams';
import phoneVerification from '@/mixins/phoneVerification';
export default {
  name: 'PhoneVerificationFirstStep',
  mixins: [dynamicForms, userAppData, redirectParams, phoneVerification],
  props: {
    countdown: {
      type: Number,
      required: false
    }
  },
  data() {
    return {
      verificationCode: '',
      phoneVerificationError: false,
      loading: false,
      loadingResend: false,
      codeInputDebounceTimeout: null,
      codeInputTimeoutedError: false
    };
  },
  validations() {
    return {
      verificationCode: {
        required,
        minLength: value => value.replaceAll(/\s/g, '').length === 6
      }
    };
  },
  methods: {
    changeNumberClick() {
      this.gtmPush({
        event: 'notification_click',
        'notification.button': this.$t('phoneVerification.changeNumber')
      });
      this.$router.push(
        this.$localePath({
          name: this.currentPath,
          query: {
            ...this.$route.query,
            pv_step: 2,
            loginDialog: this.$route.query?.loginDialog || undefined
          }
        })
      );
    },
    phoneVerification() {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.loading) {
        this.loading = true;
        this.$http
          .get(
            '/v2/authorization/validate-sms-code?code=' +
              this.verificationCode.replaceAll(/\s/g, '')
          )
          .then(async () => {
            await this.$auth.fetchUser();

            this.gtmPush({
              event: `${this.isDynamicFormStep ? 'form' : 'profile'}_verification_phone_success`
            });

            // Signál pro flow, který čeká na dokončení ověření tel. čísla před
            // změnou hesla (DEVUL-7823). Emituje se před setDialog(false), aby
            // posluchač resolvnul dřív, než zavření dialogu signalizuje zrušení.
            this.$eventHub.$emit('phoneVerified');

            if (!this.isDynamicFormStep) {
              await this.getAllDefaultFormData();
              await this.setDialog(false);
            }
          })
          .catch(err => {
            this.phoneVerificationError = true;
            this.gtmPush({
              event: `${this.isDynamicFormStep ? 'form' : 'profile'}_verification_phone_error`
            });
            this.handleError({ error: err, showModal: false, code: '0x00000004' });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    delayTouch($v) {
      this.phoneVerificationError = false;
      if (!this.codeInputTimeoutedError) {
        $v.$reset();
      }

      if (this.codeInputDebounceTimeout) {
        clearTimeout(this.codeInputDebounceTimeout);
        this.codeInputDebounceTimeout = null;
      }
      this.codeInputDebounceTimeout = setTimeout(() => {
        $v.$touch();
        this.codeInputTimeoutedError = true;
      }, 1000);
    },
    ...mapActions({
      setAuthRedirectParams: 'general/setAuthRedirectParams',
      resetProfileStore: 'profile/dashboard/resetProfileStore',
      setDialogTitle: 'profile/phoneVerification/setDialogTitle',
      setDialogLeftHeaderIcon: 'profile/phoneVerification/setDialogLeftHeaderIcon',
      setDialogButtons: 'profile/phoneVerification/setDialogButtons',
      getAllDefaultFormData: 'dynamicForms/getAllDefaultFormData',
      setDialog: 'profile/dashboard/setDialog'
    }),
    setupDialogButtons() {
      let buttons = [];
      if (!this.isDynamicFormStep) {
        buttons.push({
          props: {
            color: 'tetrary'
          },
          events: {
            click: () => {
              if (this.isDynamicFormStep) {
                this.$emit('prev-click');
              } else {
                this.changeNumberClick();
              }
            }
          },
          text: this.$t('phoneVerification.changeNumber')
        });
      }
      buttons.push({
        type: 'sendButton',
        props: {
          loading: this.loading,
          dataCy: 'phoneVerificationFirstStepSendButton'
        },
        events: {
          click: () => {
            this.phoneVerification();
          }
        },
        text: this.$t('phoneVerification.verify')
      });
      this.setDialogButtons(buttons);
    }
  },
  computed: {
    censoredPhone() {
      return `${
        this.$auth.user.phone
          ? this.$auth.user.phone.replace(this.$auth.user.phone.slice(-5, -2), '***')
          : ''
      }`;
    },
    ...mapGetters({
      authRedirectParams: 'general/authRedirectParams',
      userAppData: 'profile/userAppData/userAppData'
    })
  },
  watch: {
    loading() {
      this.setupDialogButtons();
    }
  },
  created() {
    this.$eventHub.$on('resetPhoneVerificationValidation', () => {
      this.$v.$reset();
    });
  },
  destroyed() {
    this.$eventHub.$off('resetPhoneVerificationValidation');
  },
  mounted() {
    this.setDialogTitle(this.$t('phoneVerification.title'));
    if (!this.isDynamicFormStep) {
      this.setDialogLeftHeaderIcon('icons/sms.svg');
    }
    this.setupDialogButtons();
    this.dialogScrollTop();
  }
};
</script>
